import { useAuth } from '@/contexts/AuthContext'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useOptionalTenant } from '@/contexts/TenantContext'
import { usePermissionCache } from '@/hooks/usePermissionCache'
import { Skeleton } from '@/components/ui/skeleton'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: string[]
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, userProfile, loading } = useAuth()
  const tenant = useOptionalTenant()
  const currentCompany = tenant?.currentCompany ?? null
  const isLoadingCompany = tenant?.isLoadingCompany ?? false
  const location = useLocation()
  const [showTimeout, setShowTimeout] = useState(false)
  const [hasCompanyPermission, setHasCompanyPermission] = useState<boolean | null>(null)
  const { checkCompanyPermission, getCachedPermission, isChecking } = usePermissionCache()

  // Timeout de segurança reduzido para loading infinito
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setShowTimeout(true)
      }, 5000) // 5 segundos (reduzido de 15s)
      return () => clearTimeout(timeout)
    } else {
      setShowTimeout(false)
    }
  }, [loading])

  const requiresCompanyAdmin = !!allowedRoles?.includes('company_admin')
  const hasGlobalRole = allowedRoles ? !!userProfile && allowedRoles.includes(userProfile.role) : true

  // Verificação de permissão otimizada com cache
  useEffect(() => {
    const checkPermission = async () => {
      if (!requiresCompanyAdmin) {
        setHasCompanyPermission(true)
        return
      }
      if (!user || !currentCompany?.id) return

      // Always check cache first - instant response
      const cached = getCachedPermission(user.id, currentCompany.id)
      if (cached !== null) {
        setHasCompanyPermission(cached)
        return
      }

      // Only fetch if no cache exists
      const hasPermission = await checkCompanyPermission(
        user.id,
        currentCompany.id,
        userProfile?.role
      )
      setHasCompanyPermission(hasPermission)
    }

    checkPermission()
  }, [requiresCompanyAdmin, user?.id, currentCompany?.id, userProfile?.role, checkCompanyPermission, getCachedPermission])

  if (loading && !showTimeout) {
    return (
      <div className="min-h-screen p-4 space-y-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    )
  }

  if (showTimeout) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive mb-4">
            <Loader2 className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">Timeout no carregamento</p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Recarregar página
          </button>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show skeleton if user exists but profile is still loading
  if (!userProfile) {
    return (
      <div className="min-h-screen p-4 space-y-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    )
  }

  // Se houver roles exigidas, validar
  if (allowedRoles) {
    // Super admins continuam com acesso total
    if (hasGlobalRole) {
      return <>{children}</>
    }

    // Para rotas de admin de empresa, exigir permissão na empresa atual
    if (requiresCompanyAdmin) {
      if (isLoadingCompany || isChecking || hasCompanyPermission === null) {
        return (
          <div className="min-h-screen p-4 space-y-4">
            <div className="max-w-4xl mx-auto space-y-6">
              <Skeleton className="h-14 w-full" />
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Skeleton className="h-64" />
                <div className="lg:col-span-3 space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-96 w-full" />
                </div>
              </div>
            </div>
          </div>
        )
      }

      if (!hasCompanyPermission) {
        return <Navigate to="/unauthorized" replace />
      }

      return <>{children}</>
    }

    // Caso não tenha a role necessária
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}
